package com.essntl.features.proposal.ui.addeditproposal.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import core.dateformat.utils.formatToFullString
import core.dateformat.utils.stringToLocalDate
import com.essntl.features.area.domain.model.AreaModel
import com.essntl.features.invoice.ui.allinvoice.InvoiceEvent
import com.essntl.features.invoice.ui.allinvoice.InvoiceState
import com.essntl.features.profile.ui.OwnerItem
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.model.previewLink
import com.essntl.features.proposal.domain.model.shareLink
import com.essntl.features.proposal.ui.addeditproposal.AddEditProposalEvent
import com.essntl.features.proposal.ui.addeditproposal.AddEditProposalState
import com.essntl.features.proposal.ui.addeditproposal.rememberPublishProposalEnabled
import com.essntl.features.proposal.ui.allproposal.components.ConfirmProposalPopup
import com.essntl.features.proposal.ui.allproposal.components.ProposalStatusDropDown
import com.essntl.features.proposal.ui.allproposal.components.ProposalStatusStyle
import com.essntl.toastmanager.ToastManager
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryDoubleButton
import com.essntl.uikit.components.button.referenceCode.EsRefCodeButton
import com.essntl.uikit.components.dropdown.EsDropDownMenu
import com.essntl.uikit.components.file.FileViewer
import com.essntl.uikit.components.iconbutton.secondary.EsLargeSecondaryIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.tooltip.EsTooltip
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import com.mohamedrejeb.calf.core.LocalPlatformContext
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

@Composable
fun AddEditProposalHeader(
    state: AddEditProposalState,
    onEvent: (AddEditProposalEvent) -> Unit,
    invoiceState: InvoiceState,
    onInvoiceEvent: (InvoiceEvent) -> Unit,
    selectedAreaList: List<AreaModel>,
    onSubmit: () -> Unit,
    enabled: Boolean,
) {
    val proposal = state.proposal.value

    val isEdit = remember(proposal.id) {
        proposal.id.isNotEmpty()
    }

    val openConfirmProposalDialog = rememberSaveable { mutableStateOf(false) }

    var isPublishExpanded by remember {
        mutableStateOf(false)
    }

    val context = LocalPlatformContext.current

    val publishProposalEnabled = state.rememberPublishProposalEnabled()

    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(
                vertical = EsConstants.SectionVerticalPadding,
                horizontal = EsConstants.SectionHorizontalPadding,
            ),
    ) {
        Column(
            horizontalAlignment = Alignment.Start,
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(1.spacing),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SelectionContainer {
                    Text(
                        text = proposal.title ?: "",
                        color = MaterialTheme.esColorScheme.textPrimary,
                        style = MaterialTheme.esTypography.h2Bold32,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }

                proposal.shareCode?.let { shareCode ->
                    val toastManager = koinInject<ToastManager>()
                    val scope = rememberCoroutineScope()

                    EsRefCodeButton(
                        referenceCode = shareCode,
                        onClicked = {
                            scope.launch {
                                toastManager.success(
                                    title = "Proposal reference",
                                    description = "copied to clipboard",
                                )
                            }
                        },
                    )
                }
            }
        }

        val uriHandler = LocalUriHandler.current

        Row(
            horizontalArrangement = Arrangement.spacedBy(1.spacing),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (proposal.version > 0)
                Text(
                    text = "v${proposal.version}",
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.p1Medium16,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

            OwnerItem(
                profile = proposal.owner,
                getAllProfile = state.getAllProfile,
                enabled = true,
                onOwnerChange = {
                    onEvent(
                        AddEditProposalEvent.UpdateProposalFields(
                            proposal = proposal.copy(
                                owner = it,
                            ),
                        ),
                    )
                },
            )

            if (isEdit)
                EsTooltip(
                    text = "Preview",
                ) {
                    EsLargeSecondaryIconButton(
                        onClick = {
                            // Use onNavigation here instead of this
                            uriHandler.openUri(proposal.previewLink)
                        },
                        iconRes = EsIcons.Eye,
                    )
                }

            EsTooltip(
                text = "Changed ${stringToLocalDate(proposal.statusUpdatedAt)?.formatToFullString() ?: "-"}",
            ) {
                ProposalStatusDropDown(
                    status = proposal.status ?: ProposalModel.Status.Todo,
                    style = ProposalStatusStyle.Large,
                    enabled = proposal.status != ProposalModel.Status.Confirmed,
                    onStatusSelected = { newStatus ->
                        if (newStatus != proposal.status) {
                            if (newStatus == ProposalModel.Status.Confirmed) {
                                openConfirmProposalDialog.value = true
                            } else {
                                onEvent(
                                    AddEditProposalEvent.UpdateProposalFields(
                                        proposal = proposal.copy(
                                            status = newStatus,
                                        ),
                                    ),
                                )
                            }
                        }
                    },
                )
            }

            Box {
                EsLargePrimaryDoubleButton(
                    firstTextButton = "Publish",
                    firstButtonOnClick = { isPublishExpanded = !isPublishExpanded },
                    firstSuffixIconRes = EsIcons.ArrowDown,
                    firstButtonEnabled = publishProposalEnabled &&
                        (!enabled || !state.isEnabled),
                    firstButtonShape = RoundedCornerShape(
                        topStart = EsConstants.ButtonBorderRadius,
                        bottomStart = EsConstants.ButtonBorderRadius,
                    ),
                    secondTextButton = "",
                    secondButtonOnClick = {
                        state.getProposalHistory.data?.let { p ->
                            uriHandler.openUri(p.shareLink)
                        }
                    },
                    secondButtonPrefixIconRes = EsIcons.Browse,
                    secondButtonEnabled = state.getProposalHistory.isLoading ||
                        state.getProposalHistory.data != null,
                    secondButtonLoading = state.getProposalHistory.isLoading,
                    secondButtonShape = RoundedCornerShape(
                        topEnd = EsConstants.ButtonBorderRadius,
                        bottomEnd = EsConstants.ButtonBorderRadius,
                    ),
                )

                EsDropDownMenu(
                    expanded = isPublishExpanded,
                    onDismissRequest = {
                        onEvent(AddEditProposalEvent.ClearPublish)
                        isPublishExpanded = false
                    },
                    paddingValues = PaddingValues(0.dp),
                    offset = DpOffset(
                        x = 5.dp,
                        y = 4.dp,
                    ),
                ) {
                    PublishProposal(
                        state = state,
                        onEvent = onEvent,
                        onClose = {
                            onEvent(AddEditProposalEvent.ClearPublish)
                            isPublishExpanded = false
                        },
                    )
                }
            }

            EsLargePrimaryButton(
                loading = state.addEditProposal.isLoading,
                text = "Save",
                enabled = enabled && state.isEnabled,
                onClick = onSubmit,
            )
        }
    }

    ConfirmProposalPopup(
        isOpen = openConfirmProposalDialog,
        onDismissRequest = {
            openConfirmProposalDialog.value = false
        },
        onEditProposalStatus = {
            onEvent(AddEditProposalEvent.UpdateProposalFields(proposal.copy(status = ProposalModel.Status.Confirmed)))
            onEvent(AddEditProposalEvent.EditProposal(context))
        },
    )

    val scope = rememberCoroutineScope()
    val toastManager = koinInject<ToastManager>()

    FileViewer(
        fileUrl = invoiceState.downloadQuickbooksInvoice.data?.first.orEmpty(),
        fileBaseName = invoiceState.downloadQuickbooksInvoice.data?.second?.DocNumber?.let {
            "invoice_$it"
        },
        extension = "pdf",
        isDialogOpened = invoiceState.downloadQuickbooksInvoice.isSuccess,
        onDismissRequest = {
            onInvoiceEvent(InvoiceEvent.ClearDownloadInvoice)
        },
        onDownloadSuccess = {
            val invoiceDocNumber =
                invoiceState.downloadQuickbooksInvoice.data?.second?.DocNumber?.let {
                    " $it"
                }

            scope.launch {
                toastManager.success(description = "Invoice $invoiceDocNumber downloaded!")
            }
        },
    )
}